<script lang="ts">
  import { onMount, createEventDispatcher } from 'svelte';
  import type { GameOption, BrandImage } from './types';
  import BrandHeader from './BrandHeader.svelte';
  import { darkenColor, lightenColor, generateSparklePositions, hexToRgb } from './utils';
  import { playSuccessSound } from './sounds';

  export let selectedOption: GameOption;
  export let loadingFinishedTitle: string;
  export let loadingFinishedBackgroundHex: string;
  export let image: BrandImage;
  export let cardTitle: string | undefined = undefined;

  const dispatch = createEventDispatcher<{ complete: void }>();

  let containerEl: HTMLDivElement;
  let showContent = false;
  // Pre-generate sparkle positions on mount to avoid SSR hydration mismatch
  let sparklePositions: Array<{ x: number; y: number }> = [];

  onMount(() => {
    // Generate sparkle positions client-side only
    sparklePositions = generateSparklePositions(12);

    // Play success celebration sound
    playSuccessSound();

    // Track all timeouts for cleanup
    const timeouts: ReturnType<typeof setTimeout>[] = [];

    // Trigger content animation
    timeouts.push(
      setTimeout(() => {
        showContent = true;
      }, 100)
    );

    // Dynamic import for SSR safety with error handling
    import('canvas-confetti')
      .then((module) => {
        const confetti = module.default;

        // Get container bounds for confetti origin
        const rect = containerEl.getBoundingClientRect();
        const centerX = (rect.left + rect.width / 2) / window.innerWidth;
        const centerY = (rect.top + rect.height / 2) / window.innerHeight;

        // Fire multiple confetti bursts for explosive effect
        const colors = ['#FFD700', '#FFA500', '#FF6347', '#00FF7F', '#00CED1', '#FF69B4', '#9370DB', '#ffffff'];

        // Initial big burst
        confetti({
          origin: { x: centerX, y: centerY },
          spread: 360,
          ticks: 120,
          gravity: 0.7,
          decay: 0.92,
          startVelocity: 35,
          particleCount: 80,
          scalar: 1.2,
          shapes: ['circle', 'square'],
          colors
        });

        // Secondary burst (slightly delayed)
        timeouts.push(
          setTimeout(() => {
            confetti({
              origin: { x: centerX, y: centerY },
              spread: 180,
              ticks: 100,
              gravity: 0.8,
              decay: 0.94,
              startVelocity: 25,
              particleCount: 50,
              scalar: 0.9,
              shapes: ['circle'],
              colors
            });
          }, 150)
        );

        // Third burst from sides
        timeouts.push(
          setTimeout(() => {
            confetti({
              origin: { x: centerX - 0.15, y: centerY + 0.1 },
              angle: 60,
              spread: 55,
              ticks: 80,
              gravity: 1,
              decay: 0.95,
              startVelocity: 30,
              particleCount: 30,
              scalar: 0.8,
              colors
            });
            confetti({
              origin: { x: centerX + 0.15, y: centerY + 0.1 },
              angle: 120,
              spread: 55,
              ticks: 80,
              gravity: 1,
              decay: 0.95,
              startVelocity: 30,
              particleCount: 30,
              scalar: 0.8,
              colors
            });
          }, 300)
        );
      })
      .catch((error) => {
        console.warn('Failed to load confetti library:', error);
      });

    // Transition to offer screen
    timeouts.push(
      setTimeout(() => {
        dispatch('complete');
      }, 1800)
    );

    // Cleanup all timeouts on unmount
    return () => {
      timeouts.forEach((t) => clearTimeout(t));
    };
  });
</script>

<div
  bind:this={containerEl}
  class="success-screen"
  style="--bg-color: {loadingFinishedBackgroundHex}; --bg-color-dark: {darkenColor(loadingFinishedBackgroundHex, 20)}; --bg-color-light: {lightenColor(loadingFinishedBackgroundHex, 20)}; --bg-color-rgb: {hexToRgb(loadingFinishedBackgroundHex)};"
>
  <!-- Background pattern with screen blend mode -->
  <div class="bg-pattern" aria-hidden="true"></div>

  <!-- Sparkle particles (positions generated client-side to avoid SSR hydration mismatch) -->
  <div class="sparkles" aria-hidden="true">
    {#each sparklePositions as pos, i}
      <div class="sparkle" style="--delay: {i * 0.1}s; --x: {pos.x}%; --y: {pos.y}%;"></div>
    {/each}
  </div>

  <BrandHeader {image} {cardTitle} />

  <div class="success-content" class:show={showContent} role="status" aria-live="polite">
    <div class="checkmark-wrapper" aria-hidden="true">
      <div class="checkmark-glow"></div>
      <div class="checkmark-circle">
        <svg class="checkmark" viewBox="0 0 52 52">
          <circle class="checkmark-bg" cx="26" cy="26" r="25" fill="none" />
          <path class="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
        </svg>
      </div>
    </div>

    <h2 class="success-title">{loadingFinishedTitle}</h2>

    <div class="selected-game">
      <span class="game-emoji" aria-hidden="true">{selectedOption.emoji}</span>
      <span class="game-title">{selectedOption.title}</span>
    </div>
  </div>
</div>

<style>
  .success-screen {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 16px;
    padding: 16px 24px;
    box-sizing: border-box;
    background: linear-gradient(135deg, var(--bg-color) 0%, var(--bg-color-dark) 100%);
    position: relative;
    overflow: hidden;
    container-type: size;
    container-name: success-screen;
  }

  .bg-pattern {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: 50% 75%;
    background-repeat: no-repeat;
    mix-blend-mode: screen;
    opacity: 0.12;
    pointer-events: none;
  }

  /* Sparkle particles */
  .sparkles {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    overflow: hidden;
  }

  .sparkle {
    position: absolute;
    left: var(--x);
    top: var(--y);
    width: 8px;
    height: 8px;
    background: white;
    border-radius: 50%;
    opacity: 0;
    animation: sparkle 1.5s ease-out var(--delay) forwards;
    box-shadow: 0 0 10px white, 0 0 20px var(--bg-color-light);
  }

  @keyframes sparkle {
    0% {
      opacity: 0;
      transform: scale(0) rotate(0deg);
    }
    50% {
      opacity: 1;
      transform: scale(1) rotate(180deg);
    }
    100% {
      opacity: 0;
      transform: scale(0.5) rotate(360deg) translateY(-20px);
    }
  }

  .success-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    gap: 12px;
    opacity: 0;
    transform: scale(0.5);
    transition: opacity 0.5s ease, transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.575);
    position: relative;
    z-index: 1;
  }

  .success-content.show {
    opacity: 1;
    transform: scale(1);
  }

  .checkmark-wrapper {
    position: relative;
    width: 90px;
    height: 90px;
  }

  .checkmark-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.4) 0%, transparent 70%);
    border-radius: 50%;
    animation: glowPulse 1s ease-in-out infinite;
  }

  @keyframes glowPulse {
    0%, 100% {
      transform: translate(-50%, -50%) scale(1);
      opacity: 0.6;
    }
    50% {
      transform: translate(-50%, -50%) scale(1.3);
      opacity: 1;
    }
  }

  .checkmark-circle {
    position: relative;
    width: 100%;
    height: 100%;
    animation: bounceIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.1s forwards;
    transform: scale(0);
  }

  @keyframes bounceIn {
    0% {
      transform: scale(0);
    }
    50% {
      transform: scale(1.2);
    }
    70% {
      transform: scale(0.9);
    }
    100% {
      transform: scale(1);
    }
  }

  .checkmark {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    display: block;
    stroke-width: 2;
    stroke: white;
    stroke-miterlimit: 10;
    filter: drop-shadow(0 4px 20px rgba(255, 255, 255, 0.4));
  }

  .checkmark-bg {
    stroke: rgba(255, 255, 255, 0.3);
    stroke-width: 2;
    fill: rgba(255, 255, 255, 0.15);
  }

  .checkmark-check {
    stroke-dasharray: 48;
    stroke-dashoffset: 48;
    stroke-width: 4;
    stroke-linecap: round;
    animation: drawCheck 0.4s cubic-bezier(0.65, 0, 0.45, 1) 0.5s forwards;
  }

  @keyframes drawCheck {
    100% {
      stroke-dashoffset: 0;
    }
  }

  .success-title {
    margin: 0;
    font-size: 28px;
    font-weight: 700;
    color: white;
    text-align: center;
  }

  .selected-game {
    display: flex;
    align-items: center;
    gap: 8px;
    color: white;
    padding: 8px 16px;
    border-radius: 6.4px;
  }

  .game-emoji {
    font-size: 20px;
    animation: emojiCelebrate 0.5s ease-in-out 0.8s;
  }

  @keyframes emojiCelebrate {
    0%, 100% {
      transform: scale(1) rotate(0deg);
    }
    25% {
      transform: scale(1.3) rotate(-10deg);
    }
    50% {
      transform: scale(1.3) rotate(10deg);
    }
    75% {
      transform: scale(1.2) rotate(-5deg);
    }
  }

  .game-title {
    font-size: 16px;
    font-weight: 600;
      width: max-content;
  }

  /* Wide layout */
  @container (min-aspect-ratio: 3/1) {
    .success-content {
      flex-direction: column;
      gap: 8px;
    }

    .checkmark-wrapper {
      width: 50px;
      height: 50px;
    }

    .success-title {
      font-size: 17.6px;
    }

    .selected-game {
      padding: 4.800000000000001px 12.8px;
    }

    .game-emoji {
      font-size: 16px;
    }

    .game-title {
      font-size: 13.600000000000001px;
    }
  }

  /* Ultra-short wide layout (height <= 150px, width >= 800px) - compact multi-row */
  @container success-screen (min-aspect-ratio: 3/1) {
    .success-screen {
      gap: 0;
      padding: 0 24px;
    }

    .success-content {
      position: absolute;
      top: 0;
      bottom: 0;
      left: 0;
      right: 0;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 6px;
      padding: 0 24px;
      box-sizing: border-box;
    }

    .checkmark-wrapper {
      width: 40px;
      height: 40px;
    }

    .success-title {
      font-size: 15px;
    }

    .selected-game {
      padding: 2px 8px;
    }

    .game-emoji {
      font-size: 16px;
    }

    .game-title {
      font-size: 13px;
    }

    .sparkle {
      width: 5px;
      height: 5px;
    }
  }
</style>
